import React from 'react';
import _ from 'lodash';
import {
  diffItem,
  createChangeObj,
  calculatePeriod,
  calculateItemActualCostTotal,
  calculateItemCostTotal
} from 'common/requestDetails/utils';
import {produce} from 'immer';
import {
  BOC,
  BOC_NAMES,
  DESCRIPTION,
  CHANGES_TYPES,
  UNIT_OF_MEASURE,
  PRE_AWARD,
  VALUE_CHANGE_FORMAT,
  PHASED_PROJECT,
  CARD_TYPES,
  ADDITIONAL_INFORMATION,
  COST,
  ACTUAL_TOTAL,
} from 'common/requestDetails/constants';
import pluralize from 'pluralize';
import SimpleShared from '../shared/SimpleShared';
export default class SimpleHiring extends React.Component {
  state = {
    lineItem: this.props.item,
    editMode: false,
  };

  toggleEditMode = () => this.setState({editMode: !this.state.editMode});

  handleChange = (lineItem) => {
    return new Promise((resolve) => {
      this.setState({lineItem}, () => resolve());
    });
  };

  toggleEditModeIneligibleDeferred = (value, editor) => {
    if (value && !editor) {
      this.toggleEditMode();
    }
  };

  handleBlur = (fieldName) => {
    if (this.props.editable) {
      let {lineItem} = this.state;
      let {item} = this.props;
      lineItem = _.cloneDeep(lineItem);
      let itemToDisplay = {
        ...lineItem,
        [ADDITIONAL_INFORMATION]: {
          ..._.get(item, ADDITIONAL_INFORMATION), // need to bring in additional information saved outside this component
          ..._.get(lineItem, ADDITIONAL_INFORMATION),
        },
      };

      this.setState((prevState) => ({
        lineItem: {
          ...prevState.lineItem,
          changes: itemToDisplay.changes,
          bocTotals: itemToDisplay.bocTotals,
        },
      }));
      this.props.saveItem(this.props.sourcePath, itemToDisplay, fieldName);
    }
  };

  handleItemChange = (name, value, ind = -1) => {
    if (name.startsWith(ADDITIONAL_INFORMATION + "." + ACTUAL_TOTAL)
    ) {
      return this.handleTotalCostChange(name, value);
    } 
    else {
      let newLineItem = _.cloneDeep(this.props.item);
      _.set(newLineItem, name, value);
      newLineItem = this.getItemWithChanges(this.props.originalItem, newLineItem);
      return this.handleChange(newLineItem);
    }
  };

  handleTotalCostChange = (attributeName, value) => {
    let newLineItem = _.cloneDeep(this.props.item);
    _.set(newLineItem, attributeName, value);
    const actualTotals = calculateItemActualCostTotal(newLineItem);
    _.set(newLineItem, ADDITIONAL_INFORMATION + "." + ACTUAL_TOTAL + "Sum", actualTotals );

    newLineItem = this.getItemWithChanges(this.props.originalItem, newLineItem);
    return this.handleChange(newLineItem);
  };

  handleItemChangeAndBlur = (name, value, fieldName) => {
    this.handleItemChange(name, value).then((res) => {
      this.handleBlur(fieldName);
    });
  };

  handleItemChangeAndEditMode = (name, value, fieldName) => {
    this.handleItemChangeAndBlur(name, value, fieldName);
    this.toggleEditModeIneligibleDeferred(value, this.state.editMode);
  };

  getItemWithChanges = (oldItem, newItemIn) => {
    const {itemConfig, costType} = this.props;
    const period = calculatePeriod(itemConfig);
    const isStaffingBundled = costType === CARD_TYPES.STAFFING_BUNDLED;
    const oldTotal  = oldItem?calculateItemCostTotal(oldItem):0;
    const total = calculateItemActualCostTotal(newItemIn);

    let displayCostRow = _.get(oldItem, [COST, 0], []).length > 1;
    let newItem = produce(newItemIn, (draft) => {
      return diffItem(
        draft,
        oldItem,
        _.get(itemConfig, 'labels'),
        this.props.itemCodeOptions
      );
    });

    let descriptionLabel = _.get(itemConfig, 'labels.description');
    let otherItemLabel = _.get(itemConfig, 'labels.otherDescription');
    let fullTimePositionLabel = _.get(itemConfig, 'labels.position');
    let changes = [];
    let descriptionPath = 'additionalInformation.description';

    if (oldItem) {
      const findCost = (item, year, ind) => {
        return _.get(item, [COST, year, ind], {});
      };

      if (!_.isEqual(_.get(oldItem, descriptionPath), _.get(newItem, descriptionPath))) {
        changes.push(
          createChangeObj(
            CHANGES_TYPES.VALUE_CHANGE_NO_DISPLAY,
            DESCRIPTION,
            undefined,
            undefined,
            descriptionLabel
          )
        );
      }

      if (!_.isEqual(oldTotal,total)) {
        changes.push(
          createChangeObj(
            CHANGES_TYPES.VALUE_CHANGE,
            fullTimePositionLabel,
            oldTotal,
            total,
            `Total cost for ${pluralize(fullTimePositionLabel)}`
          )
        );
      }
      

      let quantityChanged = false;
      let salaryChanged = false;
      let benefitsChanged = false;

      _.forEach(_.get(newItem, COST, []), (phaseCost, phaseInd) => {
        _.forEach(phaseCost, (newCost, ind) => {
          const oldCost = findCost(oldItem, phaseInd, ind);
          const added = _.get(newCost, 'added', false);
          let costRow = displayCostRow ? _.get(newCost, 'costRow', 0) : 0;
          if (added) {
            changes.push({
              ...createChangeObj(
                CHANGES_TYPES.NEWLY_CREATED,
                undefined,
                undefined,
                undefined,
                undefined
              ),
              costRow: costRow,
              periodType: period.type,
            });
          } 
          else {

            let boc = _.get(newCost, BOC);
            let bocOld = _.get(oldCost, BOC);

            if (boc !== bocOld) {
              changes.push({
                ...createChangeObj(
                  CHANGES_TYPES.VALUE_CHANGE,
                  BOC,
                  BOC_NAMES[bocOld],
                  BOC_NAMES[boc]
                ),
                costRow: costRow,
                periodType: period.type,
              });
            }
            const unitOfMeasure = _.get(newCost, UNIT_OF_MEASURE);
            const unitOfMeasureOld = _.get(oldCost, UNIT_OF_MEASURE);

            if (unitOfMeasure !== unitOfMeasureOld) {
              changes.push({
                ...createChangeObj(
                  CHANGES_TYPES.VALUE_CHANGE,
                  UNIT_OF_MEASURE,
                  unitOfMeasureOld,
                  unitOfMeasure
                ),
                costRow: costRow,
                periodType: period.type,
              });
            }

            const preAward = _.get(newCost, PRE_AWARD, false);
            const preAwardOld = _.get(oldCost, PRE_AWARD, false);

            if (preAward !== preAwardOld) {
              changes.push({
                ...createChangeObj(
                  CHANGES_TYPES.VALUE_CHANGE,
                  PRE_AWARD,
                  preAwardOld,
                  preAward
                ),
                costRow: costRow,
                periodType: period.type,
                formatType: VALUE_CHANGE_FORMAT.preAward,
              });
            }

            const phasedProject = _.get(newCost, PHASED_PROJECT, false);
            const phasedProjectOld = _.get(oldCost, PHASED_PROJECT, false);

            if (phasedProject !== phasedProjectOld) {
              changes.push({
                ...createChangeObj(
                  CHANGES_TYPES.VALUE_CHANGE,
                  PHASED_PROJECT,
                  phasedProjectOld,
                  phasedProject
                ),
                costRow: costRow,
                periodType: period.type,
              });
            }
          }
        });
      });
    }

    let updatedItem = produce(newItem, (draft) =>
      _.set(
        draft,
        [ADDITIONAL_INFORMATION, 'changes'],
        _.concat(_.get(newItem, 'additionalInformation.changes'), changes)
      )
    );

    return updatedItem;
  };

  renderChangesSection = () => {
    return (
      <React.Fragment />
    );
  };

  render() {
    return (
      <SimpleShared
        lineItem={this.state.lineItem}
        editMode={this.state.editMode}
        toggleEditMode={this.toggleEditMode}
        handleItemChangeAndEditMode={this.handleItemChangeAndEditMode}
        renderChangesSection={this.renderChangesSection}
        handleChange={this.handleChange}
        handleBlur={this.handleBlur}
        handleItemChange={this.handleItemChange}
        handleItemChangeAndBlur={this.handleItemChangeAndBlur}
        handleIneligibleItemChange={undefined}
        handleIneligibleItemChangeAndBlur={undefined}
        {...this.props}
      />
    );
  }
}
